import {$, $$} from '@vue-macros/reactivity-transform/macros'

import {
  IPeoplePropertyTypeInterfaces,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/PeoplePropertyTypeStrategy/types/IPeoplePropertyTypeInterfaces'
import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {usePermissionApiStore} from '@toolify/client/src/stores/api/PermissionApiStore/usePermissionApiStore'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {getParticularColorBySeed} from '@toolify/client/src/modules/color/composables/getParticularColorBySeed'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {useParticularColors} from '@toolify/client/src/modules/color/composables/useParticularColors'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {usePropertyTypeStrategy} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/usePropertyTypeStrategy'
import {IFilterPropertyItem} from '@toolify/server/src/services/FilterService/types/IFilterPropertyItem'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {isTimeAxisViewType} from '@toolify/client/src/modules/databases/functions/isTimeAxisViewType'

export function usePeopleValueTypeStrategy() {
  const strategyComponentType = PropertyStrategyComponentType.Dropdown
  const permissionApiStore = usePermissionApiStore()
  const {getColorByParticularColor} = $(useParticularColors())
  const fullValueToText = (value: IPeoplePropertyTypeInterfaces['fullValue']) => {
    if(!value) {
      return ''
    }
    return value.map(singleValue => {
      return getEntityNameByEntity(singleValue) || ''
    }).join(', ')
  }

  const fullValueToColor = (value: IPeoplePropertyTypeInterfaces['fullValue'], property?: IDatabasePropertyResponseInstance): {color: string, colorKey:string | null} | null => {
    if(!value || value.length === 0) {
      return null
    }
    const combinedIds = value?.map(item => item.entityId).join(',')
    const particularColor = getParticularColorBySeed(combinedIds)
    return {
      color: getColorByParticularColor(particularColor),
      colorKey: property ? `${property}-${combinedIds}` : null,
    }
  }

  const getAvailableValues = async (property: IDatabasePropertyResponseInstance) => {
    const res = await permissionApiStore.getMembersByResource(ResourceType.Database, property.databaseId)
    return {
      values: res.users,
      isGlobal: true,
    }
  }

  const fullValueToInputValue = (value: IPeoplePropertyTypeInterfaces['fullValue']) => {
    if(!Array.isArray(value) || value.length === 0) {
      return []
    }
    return value.map(item => item.entityId)
  }
  
  const getPossibleValueFilter = (possibleValue: IResponseEntity, group: string) => {
    return {
      type: FilterItemType.Property,
      id: group,
      operator: {
        type: FilterPropertyOperator.ArrayIncludes,
      },
      subProperty: null,
      payload: {
        type: FilterPropertyPayloadType.Static,
        value: [possibleValue.entityId],
      },
      isDisabled: false,
    }
  }

  const updateValueBySingleValueGroupSwitch = (
    currentValue: IPeoplePropertyTypeInterfaces['fullValue'],
    oldGroupValue: IPeoplePropertyTypeInterfaces['fullValue'],
    newGroupValue: IPeoplePropertyTypeInterfaces['fullValue'],
  ): string[] | null => {
    const newValue = [...(currentValue || [])].map(e => e.entityId).filter(e => e !== oldGroupValue?.[0]?.entityId).concat(newGroupValue?.[0]?.entityId || null).filter(e => !!e)
    return newValue.length ? newValue : null
  }

  const updateFullValueBySingleValueGroupSwitch = (
    currentValue: IPeoplePropertyTypeInterfaces['fullValue'],
    oldGroupValue: IPeoplePropertyTypeInterfaces['fullValue'],
    newGroupValue: IPeoplePropertyTypeInterfaces['fullValue'],
  ): IPeoplePropertyTypeInterfaces['fullValue'] | null => {
    const newValue = [...(currentValue || [])].filter(e => e.entityId !== oldGroupValue?.[0]?.entityId).concat(newGroupValue?.[0] || null).filter(e => !!e)
    return newValue.length ? newValue : null
  }

  const sortGroupItems = (a: {fullValue: IPeoplePropertyTypeInterfaces['fullValue']}, b: {fullValue: IPeoplePropertyTypeInterfaces['fullValue']}): number => {
    const aFullValue = a.fullValue
    const bFullValue = b.fullValue
    
    if (!aFullValue || !Array.isArray(aFullValue) || aFullValue.length === 0) return 1
    if (!bFullValue || !Array.isArray(bFullValue) || bFullValue.length === 0) return -1
    
    const aNicks = aFullValue
      .map(user => getEntityNameByEntity(user) || '')
      .filter(nick => nick !== '')
      .sort()
      .join(', ')
    
    const bNicks = bFullValue
      .map(user => getEntityNameByEntity(user) || '')
      .filter(nick => nick !== '')
      .sort()
      .join(', ')
    
    if (!aNicks) return 1
    if (!bNicks) return -1
    
    return aNicks.localeCompare(bNicks)
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[], databasePropertiesTableDataMap?: Map<string, unknown>): IFilter => {
    const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const searchRegex = new RegExp(escapedSearch, 'i')
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: databaseProperties.map(databaseProperty => {
        const propertyTypeStrategy = usePropertyTypeStrategy(databaseProperty.type)
        const sourcePropertyId = propertyTypeStrategy.getSourcePropertyId(databaseProperty)
        let userIds: string[] = []
        if(databasePropertiesTableDataMap?.has(sourcePropertyId)) {
          const users = databasePropertiesTableDataMap.get(sourcePropertyId) as Array<{id: string, name?: string, firstName?: string, lastName?: string, nick?: string}>
          if(users && Array.isArray(users)) {
            userIds = users
              .filter(user => {
                const name = user.name || ''
                const firstName = user.firstName || ''
                const lastName = user.lastName || ''
                const nick = user.nick || ''
                return searchRegex.test(name) || searchRegex.test(firstName) || searchRegex.test(lastName) || searchRegex.test(nick)
              })
              .map(user => user.id)
          }
        }
        if(!userIds.length) {
          return null
        }
        return {
          type: FilterItemType.Property,
          id: databaseProperty.id,
          subProperty: null,
          operator: {
            type: FilterPropertyOperator.In,
          },
          payload: {
            type: FilterPropertyPayloadType.Static,
            value: userIds,
          },
          isDisabled: false,
        } as IFilterPropertyItem
      }).filter((item): item is IFilterPropertyItem => !!item),
    }
  }

  const compareItemValues = (
    valueA: IPeoplePropertyTypeInterfaces['fullValue'],
    valueB: IPeoplePropertyTypeInterfaces['fullValue'],
    sortDirection: '1' | '-1',
  ): number => {
    const firstNickA = Array.isArray(valueA) && valueA.length ? getEntityNameByEntity(valueA[0]) || '' : ''
    const firstNickB = Array.isArray(valueB) && valueB.length ? getEntityNameByEntity(valueB[0]) || '' : ''

    if (!firstNickA && !firstNickB) return 0
    if (!firstNickA) return 1
    if (!firstNickB) return -1

    const comparison = firstNickA.localeCompare(firstNickB)

    return sortDirection === '1' ? comparison : -comparison
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table || viewType === DatabaseViewType.Kanban || isTimeAxisViewType(viewType)

  return $$({
    fullValueToText,
    fullValueToColor,
    fullValueToInputValue,
    getAvailableValues,
    getPossibleValueFilter,
    strategyComponentType,
    sortGroupItems,
    compareItemValues,
    updateValueBySingleValueGroupSwitch,
    updateFullValueBySingleValueGroupSwitch,
    searchToFilterQuery,
    canGroupByView,
  })
}
